<template>
    <div class="modal fade show" style="display:block; background:#1c1d1c55" aria-modal="true">
        <div class="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable">
            <div class="modal-content tpl-modal" style="position:relative; max-height:90vh">
                <div v-if="saveLoading" class="tpl-overlay">
                    <div class="spinner-border text-primary"></div>
                </div>

                <!-- Header -->
                <div class="tpl-header d-flex justify-content-between align-items-start px-3 py-2">
                    <div>
                        <h5 class="mb-0 fw-semibold">{{ template ? template.name : '' }}</h5>
                        <small class="text-muted">Template Accounts</small>
                    </div>
                    <button @click="$emit('onClose')" type="button" class="btn-close mt-1"></button>
                </div>

                <!-- Toolbar -->
                <div v-if="!loading && treeNodes.length > 0" class="tpl-toolbar d-flex align-items-center gap-2 px-3 py-2">
                    <div class="ms-auto d-flex gap-1">
                        <button class="btn btn-outline-secondary btn-xs" @click="setAllExpanded(true)">Expand All</button>
                        <button class="btn btn-outline-secondary btn-xs" @click="setAllExpanded(false)">Collapse All</button>
                    </div>
                </div>

                <!-- Body -->
                <div class="modal-body py-2 px-3" style="overflow-y:auto; flex:1">
                    <div v-if="loading" class="text-center py-5">
                        <div class="spinner-border text-primary mb-2"></div>
                        <div class="text-muted small">Loading accounts…</div>
                    </div>
                    <div v-else-if="treeNodes.length === 0" class="text-center text-muted py-5">
                        <i class="fa fa-folder-open fa-2x mb-2 d-block opacity-50"></i>
                        No accounts found in this template.
                    </div>
                    <div v-else>
                        <TemplateTreeNode
                            v-for="node in rootNodes"
                            :key="node.id"
                            :node="node"
                            :childMap="childMap"
                            :depth="0"
                            :expandAll="expandAll"
                            @remove="removeNode"
                        />
                    </div>
                </div>

                <!-- Footer -->
                <div class="tpl-footer px-3 py-2 d-flex gap-2 justify-content-end align-items-center">
                    <button @click="$emit('onClose')" class="btn btn-outline-secondary btn-sm">Cancel</button>
                    <button
                        v-if="projectId && treeNodes.length > 0"
                        @click="handleSave"
                        :disabled="saveLoading"
                        class="btn btn-primary btn-sm"
                    >
                        <span v-if="saveLoading" class="spinner-border spinner-border-sm me-1"></span>
                        Save & Import
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, inject, computed } from 'vue';
import TemplateTreeNode from '@/components/molecule/procurement/project/TemplateTreeNode.vue';
import { useRoute } from 'vue-router';
import handleProjects from '@/services/modules/procurement/project';
import handleTemplate from '@/services/modules/all-modules/template';

const props = defineProps({
    template: { type: Object, default: null },
    accountType: { type: String, required: true },
    canDelete: { type: Boolean, default: true },
    projectId: { type: [Number, String], default: null },
});

const emit = defineEmits(['onClose', 'onDeleted', 'onSaved']);

const route = useRoute();
const showError = inject('showError');
const showSuccess = inject('showSuccess');

const { fetchTemplateAccountTree, accountCloneFromTemplate } = handleProjects();
const { removeTemplate } = handleTemplate();

const treeNodes = ref([]);
const loading = ref(false);
const saveLoading = ref(false);
const expandAll = ref(null);

const rootNodes = computed(() => treeNodes.value.filter(n => !n.parent_id));

const childMap = computed(() => {
    const map = {};
    treeNodes.value.forEach(n => {
        if (n.parent_id) {
            if (!map[n.parent_id]) map[n.parent_id] = [];
            map[n.parent_id].push(n);
        }
    });
    return map;
});

watch(() => props.template, (tpl) => {
    if (tpl) loadAccounts(tpl.templateable_id);
    else treeNodes.value = [];
}, { immediate: true });

function getQuery() {
  let query = `?company_id=${route.params.companyId}`;
  query += `&from_project=1`

  if (!props.accountType) {
    return query;
  }

  let type = props.accountType;
  if (type === 'activity_target') {
    return query + `&account_type=activity&project_type=task,donor_kpi`;
  }

  if (type === 'activity_budget') {
    return query + `&account_type=activity`;
  }

  return query + `&account_type=${type.toLowerCase()}`;
}

async function loadAccounts(projectId) {
    loading.value = true;
    treeNodes.value = [];
    const query = getQuery();

    const res = await fetchTemplateAccountTree(projectId, query).catch(() => null);
    loading.value = false;
    if (!res || !res.status) return;

    const flat = res.data;

    const withParent = buildParentRefs(flat);
    treeNodes.value = withParent;
}

function buildParentRefs(flat) {
    const byId = {};
    flat.forEach(item => { byId[item.id] = { ...item, parent_id: item.project_account_id || null }; });
    return Object.values(byId);
}

function removeNode(id) {
    const toRemove = new Set();
    collectDescendants(id, toRemove);
    treeNodes.value = treeNodes.value.filter(n => !toRemove.has(n.id));
}

function collectDescendants(id, set) {
    set.add(id);
    treeNodes.value.forEach(n => {
        if (n.parent_id === id) collectDescendants(n.id, set);
    });
}

function setAllExpanded(val) {
    expandAll.value = val ? Date.now() + '_expand' : Date.now() + '_collapse';
}

async function handleSave() {
    if (!props.projectId) return;
    saveLoading.value = true;
    const query = `?company_id=${route.params.companyId}&account_type=${props.accountType}`;
    const items = treeNodes.value.map(n => ({
        id: n.id,
        parent_id: n.parent_id,
        name: n.name,
        code: n.code,
        description: n.description,
        type: n.type,
        group_level: n.group_level,
        project_account_type: n.project_account_type,
        project_type: n.project_type,
    }));

    try {
        const res = await accountCloneFromTemplate(props.projectId, query, { items });
        if (!res.status) {
            showError(res.message);
            return;
        }
        showSuccess(res.message);
        emit('onSaved');
        emit('onClose');
    } catch {
        showError('Something went wrong');
    } finally {
        saveLoading.value = false;
    }
}

</script>

<style scoped>
.tpl-modal { border-radius: 12px; }
.tpl-overlay {
    position: absolute; inset: 0;
    background: rgba(255,255,255,0.75);
    z-index: 10;
    display: flex; align-items: center; justify-content: center;
    border-radius: inherit;
}
.tpl-header {
    border-bottom: 1px solid #e9ecef;
    background: #f8f9fa;
    border-radius: 12px 12px 0 0;
}
.tpl-toolbar {
    border-bottom: 1px solid #e9ecef;
    background: #fff;
    font-size: 0.82rem;
}
.tpl-footer {
    border-top: 1px solid #e9ecef;
    background: #f8f9fa;
    border-radius: 0 0 12px 12px;
}
.btn-xs {
    font-size: 0.75rem;
    padding: 2px 8px;
    line-height: 1.4;
    border-radius: 4px;
}
</style>
